// Phase 5Z.42 — Client-side image compressor / scaler.
//
// Resizes images to a max dimension (default 1920px) and re-encodes as JPEG
// with configurable quality (default 0.85). Drastically reduces upload size
// and DB/storage costs for driver photos, vehicle damages, signatures, logos.
//
// Usage:
//   import { compressImage } from "@/lib/imageCompressor";
//   const compressed = await compressImage(file);            // → File (<File>)
//   const dataUrl   = await compressImageToDataUrl(file);   // → string
//
// Behaviour:
//   - Skips non-image files (returns the original).
//   - Skips already-small images (<= maxDimension AND smaller than threshold).
//   - PNG with transparency: kept as PNG only when keepPngForTransparency=true,
//     otherwise re-encoded as JPEG (default — saves ~70% size).
//   - HEIC/HEIF: returned untouched (browser-decode is not universally supported).

const DEFAULT_OPTS = {
  maxDimension: 1920,            // longest side
  jpegQuality: 0.85,             // 0–1 — sweet spot for photos
  minBytesToCompress: 200 * 1024, // < 200 KB → skip (already small)
  outputType: "image/jpeg",
  keepPngForTransparency: false,
};

/** Read a File/Blob as an HTMLImageElement (decoded). */
function readAsImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/** Resize the image onto a canvas and return the resulting Blob. */
function drawAndExport(img, maxDimension, outputType, jpegQuality) {
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  const longest = Math.max(w0, h0);
  let w = w0;
  let h = h0;
  if (longest > maxDimension) {
    const scale = maxDimension / longest;
    w = Math.round(w0 * scale);
    h = Math.round(h0 * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  // Improve scale-down quality (default is "low")
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // White background avoids transparent JPEG → black artifact
  if (outputType === "image/jpeg") {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))),
      outputType,
      jpegQuality,
    );
  });
}

/** Sanitise the filename: strip extension, append .jpg/.png. */
function rename(filename, ext) {
  const stem = (filename || "image").replace(/\.[^.]+$/, "");
  return `${stem}.${ext}`;
}

/**
 * Compress an image File / Blob.
 * @param {File|Blob} file
 * @param {object} [opts]
 * @returns {Promise<File>} the (possibly compressed) file. Falls back to the
 *   original if compression fails or is skipped.
 */
export async function compressImage(file, opts = {}) {
  if (!file) return file;
  const o = { ...DEFAULT_OPTS, ...opts };

  // Type check
  const isImage = (file.type || "").startsWith("image/");
  if (!isImage) return file;

  // Skip HEIC — browser decoder can't render natively (would silently break)
  if (/(heic|heif)/i.test(file.type)) return file;

  // Skip already-small images
  if (file.size <= o.minBytesToCompress) return file;

  // Decide output type
  const isPng = file.type === "image/png";
  const outputType = isPng && o.keepPngForTransparency ? "image/png" : o.outputType;

  try {
    const img = await readAsImage(file);
    const blob = await drawAndExport(img, o.maxDimension, outputType, o.jpegQuality);

    // If the compressed blob is somehow larger than the original, fall back
    if (blob.size >= file.size) return file;

    const ext = outputType === "image/png" ? "png" : "jpg";
    const out = new File([blob], rename(file.name || "image", ext), {
      type: outputType,
      lastModified: Date.now(),
    });
    return out;
  } catch {
    // On any error (corrupted image, exotic codec) keep the original
    return file;
  }
}

/** Convert a File/Blob to a base64 dataURL. */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Compress + return as dataURL (handy for legacy <img src=…> previews). */
export async function compressImageToDataUrl(file, opts = {}) {
  const compressed = await compressImage(file, opts);
  return fileToDataUrl(compressed);
}

/** Human-readable byte string (e.g. "1.4 MB"). Used in console-debug toasts. */
export function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
